import { PortalJob, ExtensionJob } from '../types/portal';

export async function fetchPortalJobs(): Promise<PortalJob[]> {
  const url = '/api/portal-data?type=peReturns';

  const resp = await fetch(url, {
    method: 'GET',
    cache: 'no-store',
    credentials: 'include',
  });

  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`Failed to load jobs (HTTP ${resp.status}). ${text}`);
  }

  const data = (await resp.json()) as unknown;
  if (!Array.isArray(data)) return [];
  return data as PortalJob[];
}

// ---- Cache layer ----
const CACHE_KEY = 'portalJobsCache.v1';
const TTL_MS = 15 * 60 * 1000;

// In-memory cache (fast during the same tab session)
let memData: PortalJob[] | null = null;
let memFetchedAt = 0;

type CachePayload = {
  fetchedAt: number;
  data: PortalJob[];
};

export function getCachedPortalJobs(): { data: PortalJob[] | null; fetchedAt: number; isFresh: boolean } {
  // Prefer in-memory first
  if (memData && memFetchedAt) {
    const isFresh = Date.now() - memFetchedAt < TTL_MS;
    return { data: memData, fetchedAt: memFetchedAt, isFresh };
  }

  // Fallback to sessionStorage
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return { data: null, fetchedAt: 0, isFresh: false };
    const parsed = JSON.parse(raw) as CachePayload;

    if (!parsed?.data || !Array.isArray(parsed.data) || !parsed.fetchedAt) {
      return { data: null, fetchedAt: 0, isFresh: false };
    }

    // hydrate memory
    memData = parsed.data;
    memFetchedAt = parsed.fetchedAt;

    const isFresh = Date.now() - parsed.fetchedAt < TTL_MS;
    return { data: parsed.data, fetchedAt: parsed.fetchedAt, isFresh };
  } catch {
    return { data: null, fetchedAt: 0, isFresh: false };
  }
}

export function setCachedPortalJobs(data: PortalJob[]) {
  const payload: CachePayload = { fetchedAt: Date.now(), data };
  memData = data;
  memFetchedAt = payload.fetchedAt;
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(payload));
  } catch {
    // ignore storage failures
  }
}

/**
 * Uses cache when fresh. If stale/empty, fetches from network and resets timer.
 *
 * This matches your requirement:
 * - No refetch when moving between screens within 15 minutes.
 * - After 15 minutes, it refreshes *next time* you come back to JobsDashboard (mount).
 */
export async function fetchPortalJobsCached(): Promise<{ data: PortalJob[]; fromCache: boolean }> {
  const cached = getCachedPortalJobs();
  if (cached.data && cached.isFresh) {
    return { data: cached.data, fromCache: true };
  }

  const data = await fetchPortalJobs();
  setCachedPortalJobs(data);
  return { data, fromCache: false };
}

// ─── Extension Jobs ───────────────────────────────────────────────────────────

export async function fetchExtensionJobs(): Promise<ExtensionJob[]> {
  const url = '/api/portal-data?type=extensionReturns&jobIdx=0';

  const resp = await fetch(url, {
    method: 'GET',
    cache: 'no-store',
    credentials: 'include',
  });

  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`Failed to load extension jobs (HTTP ${resp.status}). ${text}`);
  }

  const data = (await resp.json()) as unknown;
  if (!Array.isArray(data)) return [];
  return data as ExtensionJob[];
}

const EXT_CACHE_KEY = 'extensionJobsCache.v1';

let extMemData: ExtensionJob[] | null = null;
let extMemFetchedAt = 0;

type ExtCachePayload = {
  fetchedAt: number;
  data: ExtensionJob[];
};

export function getCachedExtensionJobs(): { data: ExtensionJob[] | null; fetchedAt: number; isFresh: boolean } {
  if (extMemData && extMemFetchedAt) {
    const isFresh = Date.now() - extMemFetchedAt < TTL_MS;
    return { data: extMemData, fetchedAt: extMemFetchedAt, isFresh };
  }
  try {
    const raw = sessionStorage.getItem(EXT_CACHE_KEY);
    if (!raw) return { data: null, fetchedAt: 0, isFresh: false };
    const parsed = JSON.parse(raw) as ExtCachePayload;
    if (!parsed?.data || !Array.isArray(parsed.data) || !parsed.fetchedAt) {
      return { data: null, fetchedAt: 0, isFresh: false };
    }
    extMemData = parsed.data;
    extMemFetchedAt = parsed.fetchedAt;
    const isFresh = Date.now() - parsed.fetchedAt < TTL_MS;
    return { data: parsed.data, fetchedAt: parsed.fetchedAt, isFresh };
  } catch {
    return { data: null, fetchedAt: 0, isFresh: false };
  }
}

export function setCachedExtensionJobs(data: ExtensionJob[]) {
  const payload: ExtCachePayload = { fetchedAt: Date.now(), data };
  extMemData = data;
  extMemFetchedAt = payload.fetchedAt;
  try {
    sessionStorage.setItem(EXT_CACHE_KEY, JSON.stringify(payload));
  } catch {
    // ignore storage failures
  }
}

export async function fetchExtensionJobsCached(): Promise<{ data: ExtensionJob[]; fromCache: boolean }> {
  const cached = getCachedExtensionJobs();
  if (cached.data && cached.isFresh) {
    return { data: cached.data, fromCache: true };
  }

  const data = await fetchExtensionJobs();
  setCachedExtensionJobs(data);
  return { data, fromCache: false };
}